import React, { useEffect, useState } from 'react';
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Button, Col, Form as FormLayout, ToggleButton } from "react-bootstrap";
import TextInput from "../../../../../Common/components/text-input";
import { statusColourName } from "../../../../../../util/Helpers";
import SelectInput from "../../../../../Common/components/select-input";
import {
    useApproveDocumentMutation,
    useInitiateCipCheckMutation,
    useRejectDocumentMutation,
    useResetDocumentMutation,
    useUpdateDocumentFieldsMutation,
} from "../../../../../../../redux/apiSlice";
import cogoToast from "cogo-toast";
import { defaultRejectReason } from "../../../../data/DefaultRejectReason";
import { useEffectAfterMount } from "../../../../../../util/Hooks";
import MagnifiableImage from '../../../../../Common/components/MagnifiableImage';

function Document(
    {
        initialValues,
        checkResult,
        isEditable,
    }
) {
    const [rejectAction, setRejectAction] = useState(false);

    const [
        approveDocument, // This is the mutation trigger
        {
            isLoading: isApproving,
            isSuccess: approvedSuccessfully,
        }, // This is the destructured mutation result
    ] = useApproveDocumentMutation()

    /* Go Back if Approval is Successful */
    useEffect(() => {
        if (approvedSuccessfully) {
            cogoToast.success("Document Approved");
        }
    }, [approvedSuccessfully]);

    const [
        rejectDocument, // This is the mutation trigger
        {
            isLoading: isRejecting,
            isSuccess: rejectedSuccessfully,
        }, // This is the destructured mutation result
    ] = useRejectDocumentMutation()

    /* Go Back if Rejection is Successful */
    useEffect(() => {
        if (rejectedSuccessfully) {
            cogoToast.success("Document Rejected");
        }
    }, [rejectedSuccessfully]);

    const [
        resetDocument, // This is the mutation trigger
        {
            isLoading: isResetting,
            isSuccess: resetSuccessfully,
        }, // This is the destructured mutation result
    ] = useResetDocumentMutation()

    /* Go Back if Reset is Successful */
    useEffect(() => {
        if (resetSuccessfully) {
            cogoToast.success("Document Reset to Pending");
        }
    }, [resetSuccessfully]);

    const [
        updateDocument, // This is the mutation trigger
        {
            isLoading: isUpdating,
            isSuccess: updatedSuccessfully,
        }, // This is the destructured mutation result
    ] = useUpdateDocumentFieldsMutation()

    /* Go Back if Rejection is Successful */
    useEffect(() => {
        if (updatedSuccessfully) {
            cogoToast.success("Document Fields Updated");
        }
    }, [updatedSuccessfully]);

    const [
        initiateCheck, // This is the mutation trigger
        {
            isLoading: isInitiating,
            isSuccess: initiatedSuccessfully,
        }, // This is the destructured mutation result
    ] = useInitiateCipCheckMutation()

    /* Show Toast if check is initialized successfully */
    useEffect(() => {
        if (initiatedSuccessfully) {
            cogoToast.success("CIP Check Initiated");
        }
    }, [initiatedSuccessfully]);

    return (
        <div className={"d-flex flex-column h-100"}>
            <div className={"rounded-lg flex-fill bg-light-secondary mb-4"}>
                <MagnifiableImage height={"100%"} width={"100%"} src={initialValues?.document} alt={initialValues?.type ?? ""} />
            </div>
            {
                !rejectAction
                    ? <EditDocument
                        checkResult={checkResult}
                        initialValues={initialValues}
                        onApprove={approveDocument}
                        isApproving={isApproving}
                        onReject={() => setRejectAction(true)}
                        onReset={resetDocument}
                        isResetting={isResetting}
                        onInitiateCheck={initiateCheck}
                        isInitiating={isInitiating}
                        onUpdateDocument={updateDocument}
                        isUpdating={isUpdating}
                        isEditable={isEditable}
                    />
                    : <RejectDocument
                        initialValues={initialValues}
                        onReject={rejectDocument}
                        isRejecting={isRejecting}
                        isRejected={rejectedSuccessfully}
                        onCancel={() => setRejectAction(false)}
                        isEditable={isEditable}
                    />
            }
        </div>
    )
}

export function EditDocument(
    {
        initialValues,
        checkResult,
        onReset,
        isResetting,
        onApprove,
        isApproving,
        onReject,
        onInitiateCheck,
        isInitiating,
        onUpdateDocument,
        isUpdating,
        isEditable,
    }
) {
    const validationSchema = Yup.object({
        documentId: Yup.string().required('Required'),
        userId: Yup.string().required('Required'),
        userGroup: Yup.string().required('Required'),
        type: Yup.string().required('Required'),
        idType: Yup.string().required('Required'),
        idValue: Yup.string().when('type', {
            is: (type) => type?.startsWith("PICTURE_ID"),
            then: Yup.string().required('Required')
        }),
        side: Yup.string().required('Required'),
    })

    const extractFormData = (keys, source) => {
        const data = {}
        for (const key of keys) {
            data[key] = source[key] || ""
        }

        return data
    }

    const compiledInitialValues = extractFormData(
        [
            "documentId",
            "userId",
            "userGroup",
            "type",
            "idType",
            "idValue",
            "status",
            "side"
        ],
        initialValues
    )

    compiledInitialValues.side = compiledInitialValues.side || "front"

    const validStyle = statusColourName(checkResult?.result || "pending")

    let cipStatus = "Documents check uninitialized."
    switch (checkResult?.result) {
        case "failed":
            cipStatus = "Documents check failed."
            break
        case "consider":
            cipStatus = "Documents check not passed."
            break
        case "clear":
            cipStatus = "Documents check clear."
            break
        default:
            cipStatus = "Documents check failed."
            break

    }

    return (
        <Formik
            validationSchema={validationSchema}
            initialValues={compiledInitialValues}
            validateOnMount={true}
            onSubmit={(values) => {
                values.meta = initialValues
                onApprove(values)
            }}
            enableReinitialize={true}
        >
            {({ isValid, dirty, values }) =>
                <Form className={"pt-4 border-top"} style={{ height: "50%" }}>
                    <fieldset disabled={!isEditable} className={"h-100 d-flex flex-column"}>
                        <div className={"flex-fill"}>
                            <div
                                className={`d-flex align-items-center w-100 p-3 mb-3 rounded-sm text-${validStyle} bg-light-${validStyle}`}
                            >
                                <span className={"flex-fill font-weight-bold"}>
                                    {cipStatus}
                                </span>
                            </div>
                            <FormLayout.Row>
                                <SelectInput
                                    label="Category*"
                                    name="type"
                                    options={[
                                        "PICTURE_ID",
                                        "PROOF_OF_ADDRESS",
                                        "PICTURE_ID_PROOF_OF_ADDRESS",
                                        "TAX",
                                        "TRUST_DOCUMENTS",
                                        "PASSPORT",
                                    ]}
                                />
                                <TextInput
                                    label="ID Type*"
                                    name="idType"
                                    type="text"
                                />
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <TextInput
                                    label={`ID Number ${values?.type?.startsWith("PICTURE_ID") ? "*" : ""}`}
                                    name="idValue"
                                    type="text"
                                />
                                <SelectInput
                                    label="Side*"
                                    name="side"
                                    options={[
                                        "front",
                                        "back"
                                    ]}
                                />
                            </FormLayout.Row>
                            {
                                initialValues.status === "REJECTED" &&
                                <div className='text-danger'>
                                    Reason for rejection: {initialValues.rejectionReason}
                                </div>
                            }
                        </div>
                        {
                            (dirty && isEditable) && <FormLayout.Row className={"mt-6"}>
                                <Button
                                    type="button"
                                    variant={"dark"}
                                    className={"w-100"}
                                    onClick={() => onUpdateDocument(
                                        {
                                            ...values,
                                            meta: values
                                        }
                                    )}
                                    disabled={!isValid || isUpdating}
                                >
                                    Apply Changes
                                </Button>
                            </FormLayout.Row>
                        }
                        {
                            (!dirty && isEditable) && <FormLayout.Row className={"mt-6"}>
                                {
                                    (initialValues.status !== "PENDING") && <Col>
                                        <Button
                                            type="button"
                                            variant={"outline-dark"}
                                            className={"w-100"}
                                            onClick={() => onReset({...values, meta: initialValues})}
                                            disabled={isResetting}
                                        >
                                            Reset
                                        </Button>
                                    </Col>
                                }
                                {
                                    initialValues.status !== "REJECTED" && <Col>
                                        <Button
                                            type="button"
                                            variant={"outline-dark"}
                                            className={"w-100"}
                                            onClick={onReject}
                                        >
                                            Reject
                                        </Button>
                                    </Col>
                                }
                                {
                                    initialValues.status !== "APPROVED" && <Col>
                                        <Button
                                            type="submit"
                                            variant={"dark"}
                                            className={"w-100"}
                                            disabled={!isValid || isApproving}
                                        >
                                            Approve
                                        </Button>
                                    </Col>
                                }
                            </FormLayout.Row>
                        }
                    </fieldset>
                </Form>
            }
        </Formik>
    );
}

export function RejectDocument(
    {
        initialValues,
        isRejecting,
        isRejected,
        onReject,
        onCancel,
        isEditable,
    }
) {
    const validationSchema = Yup.object({
        rejectionReason: Yup.string().required('Required'),
    })

    const compiledInitialValues = {
        documentId: "",
        rejectionReason: "",
        ...initialValues,
    }

    useEffectAfterMount(() => {
        isRejected && onCancel()
    }, [isRejected])

    return (
        <Formik
            validationSchema={validationSchema}
            initialValues={compiledInitialValues}
            validateOnMount={true}
            onSubmit={(values) => {
                values.meta = initialValues
                onReject(values)
            }}
        >
            {({ isValid, values, setFieldValue }) =>
                <Form className={"pt-4 border-top d-flex flex-column"} style={{ height: "50%" }}>
                    <fieldset disabled={!isEditable}>
                        <div className={"flex-fill"}>
                            <FormLayout.Row>
                                <TextInput
                                    label="Rejection Reason*"
                                    name="rejectionReason"
                                    type="text"
                                    disabled={isRejecting}
                                />
                            </FormLayout.Row>
                            <FormLayout.Row className="pt-1 pb-10">
                                {defaultRejectReason.map(({ id, text }, i) => (
                                    <ToggleButton
                                        key={id}
                                        id={"rejectionReason"}
                                        type="radio"
                                        name={text}
                                        value={text}
                                        variant={`outline-${values.rejectionReason === text ? "danger" : "secondary"}`}
                                        className={"mr-2"}
                                        checked={values.rejectionReason === text}
                                        onChange={(e) => setFieldValue("rejectionReason", e.currentTarget.value)}
                                    >
                                        {text}
                                    </ToggleButton>
                                ))}
                            </FormLayout.Row>
                        </div>
                        {
                            isEditable && <FormLayout.Row className={"mt-6"}>
                                <Col>
                                    <Button
                                        type="button"
                                        variant={"outline-dark"}
                                        className={"w-100"}
                                        onClick={onCancel}
                                    >
                                        Cancel
                                    </Button>
                                </Col>
                                <Col>
                                    <Button
                                        type="submit"
                                        variant={"dark"}
                                        className={"w-100"}
                                        disabled={!isValid || isRejecting}
                                    >
                                        Reject
                                    </Button>
                                </Col>
                            </FormLayout.Row>
                        }
                    </fieldset>
                </Form>
            }
        </Formik>
    );
}

export default Document;

